import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardCheck, Download, FileSpreadsheet, FileText, LogOut, MessageSquareWarning, ShieldCheck, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import ComplianceReportView from '../../components/compliance/ComplianceReportView';
import ChecklistView from '../../components/compliance/ChecklistView';
import auditorService, { AuditorError } from '../../services/auditor';
import { formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';
import {
  FINDING_TYPE_LABELS, type AuditFinding, type AuditorEngagementInfo, type AutoChecks, type Checklist,
  type ComplianceReport, type FindingType, type InvitePreview,
} from '../../types/compliance';

/**
 * /audit/:token — the external auditor's view. Public route, no FactreeTech
 * account: the link identifies the engagement, a code emailed to the address
 * the organisation named signs them in, and from then on they see exactly the
 * records in scope, read-only. They can download the PDF, and record findings
 * the organisation answers.
 */

type Stage = 'loading' | 'email' | 'code' | 'ready' | 'closed';
type TabKey = 'records' | 'checklist' | 'findings';

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-primary-700 text-white">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-4 py-3">
          <ShieldCheck className="h-5 w-5 shrink-0" />
          <span className="font-semibold">FactreeTech</span>
          <span className="truncate text-sm text-primary-100">· read-only audit access</span>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
    </div>
  );
}

const inputClass = 'w-full min-w-0 rounded-lg border border-neutral-300 px-3 py-2 text-sm';

function Findings({ sections, tz }: { sections: { key: string; label: string }[]; tz: string }) {
  const [list, setList] = useState<AuditFinding[]>([]);
  const [form, setForm] = useState<{ clause_ref: string; section: string; finding: string; finding_type: FindingType }>({
    clause_ref: '', section: 'general', finding: '', finding_type: 'observation',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => { auditorService.listFindings().then(setList).catch(() => setList([])); }, []);
  useEffect(load, [load]);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await auditorService.addFinding({
        clause_ref: form.clause_ref.trim() || null, section: form.section, finding: form.finding.trim(), finding_type: form.finding_type,
      });
      setForm({ ...form, clause_ref: '', finding: '' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (id: number) => {
    try { await auditorService.withdrawFinding(id); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not withdraw'); }
  };

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <p className="text-sm text-neutral-600">Findings go straight to the organisation, which answers each one here.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block min-w-0"><span className="mb-1 block text-sm font-medium text-neutral-700">Type</span>
            <select className={inputClass} value={form.finding_type} onChange={e => setForm({ ...form, finding_type: e.target.value as FindingType })}>
              {Object.entries(FINDING_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="block min-w-0"><span className="mb-1 block text-sm font-medium text-neutral-700">Area</span>
            <select className={inputClass} value={form.section} onChange={e => setForm({ ...form, section: e.target.value })}>
              <option value="general">General</option>
              {sections.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              <option value="checklist">Checklist</option>
            </select>
          </label>
          <label className="block min-w-0"><span className="mb-1 block text-sm font-medium text-neutral-700">Clause (optional)</span>
            <input className={inputClass} value={form.clause_ref} maxLength={64} onChange={e => setForm({ ...form, clause_ref: e.target.value })} />
          </label>
        </div>
        <label className="block"><span className="mb-1 block text-sm font-medium text-neutral-700">Finding</span>
          <textarea className={inputClass} rows={3} maxLength={4000} value={form.finding} onChange={e => setForm({ ...form, finding: e.target.value })} />
        </label>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <Button onClick={add} disabled={saving || !form.finding.trim()}>{saving ? 'Saving…' : 'Record finding'}</Button>
      </section>
      <section>
        <h2 className="mb-2 text-base font-semibold text-neutral-900">Your findings ({list.length})</h2>
        {list.length === 0 ? <p className="text-sm text-neutral-500">None recorded yet.</p> : (
          <ul className="space-y-3">
            {list.map(f => (
              <li key={f.id} className="rounded-lg border border-neutral-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">{FINDING_TYPE_LABELS[f.finding_type]}{f.clause_ref ? ` · ${f.clause_ref}` : ''}</p>
                  <span className="text-xs text-neutral-500">{f.status}{f.corrective_action_raised ? ' · corrective action raised' : ''}</span>
                </div>
                <p className="mt-1 whitespace-pre-line break-words text-sm text-neutral-800">{f.finding}</p>
                <p className="mt-1 text-xs text-neutral-500">{formatDateTimeInTz(f.created_at, tz)}</p>
                {f.org_response ? (
                  <p className="mt-2 whitespace-pre-line break-words rounded bg-neutral-50 p-2 text-sm text-neutral-700"><span className="font-medium">Response: </span>{f.org_response}</p>
                ) : (
                  <button onClick={() => withdraw(f.id)} className="mt-2 inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" />Withdraw</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default function AuditorPortal() {
  const { token = '' } = useParams();
  const [stage, setStage] = useState<Stage>('loading');
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<AuditorEngagementInfo | null>(null);
  const [data, setData] = useState<{ report: ComplianceReport; checks: AutoChecks; checklist: Checklist | null } | null>(null);
  const [tab, setTab] = useState<TabKey>('records');

  const fail = useCallback((e: unknown) => {
    const err = e instanceof AuditorError ? e : null;
    if (err && (err.status === 401 || err.status === 403)) {
      auditorService.signOut();
      setData(null);
      setError(err.message);
      setStage(err.code === 'AUDIT_ACCESS_ENDED' ? 'closed' : 'email');
    } else {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    }
  }, []);

  const loadRecords = useCallback(async () => {
    setStage('loading');
    try {
      const [i, d] = await Promise.all([auditorService.getEngagement(), auditorService.getReport()]);
      setInfo(i);
      setData(d);
      setStage('ready');
    } catch (e) { fail(e); }
  }, [fail]);

  useEffect(() => {
    if (auditorService.resume(token)) { loadRecords(); return; }
    auditorService.getInvite(token)
      .then(p => { setInvite(p); setStage(p.status === 'active' ? 'email' : 'closed'); })
      .catch(e => { setError(e instanceof Error ? e.message : 'This link is not valid'); setStage('closed'); });
  }, [token, loadRecords]);

  const sendCode = async () => {
    setBusy(true); setError(null);
    try {
      const r = await auditorService.requestCode(token, email);
      setMessage(r.message);
      setStage('code');
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError(null);
    try { await auditorService.verifyCode(token, code); await loadRecords(); }
    catch (e) { setError(e instanceof Error ? e.message : 'That code did not work'); }
    finally { setBusy(false); }
  };

  const download = async (kind: 'pdf' | 'csv') => {
    if (!info) return;
    const base = info.title.replace(/[^\w.-]+/g, '-').slice(0, 80);
    try { await auditorService.download(kind, kind === 'pdf' ? `${base}.pdf` : `${base}-spray-register.csv`); }
    catch (e) { fail(e); }
  };

  if (stage === 'loading') {
    return <Shell><div className="h-40 animate-pulse rounded-xl bg-neutral-100" /></Shell>;
  }

  if (stage === 'closed') {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-700">
          <h1 className="mb-2 text-lg font-semibold text-neutral-900">This access is not open</h1>
          <p>{error ?? (invite ? `${invite.organisation} has ended this access, or it has not started yet.` : 'This link is not valid.')}</p>
          <p className="mt-2">Ask the organisation for a new link if you still need the records.</p>
        </div>
      </Shell>
    );
  }

  if (stage === 'email' || stage === 'code') {
    return (
      <Shell>
        <div className="mx-auto max-w-md space-y-4 rounded-xl border border-neutral-200 bg-white p-6">
          <div>
            <h1 className="text-lg font-semibold text-neutral-900">{invite?.organisation ?? 'Audit records'}</h1>
            {invite && <p className="break-words text-sm text-neutral-600">{invite.title} · for {invite.auditor_name}</p>}
          </div>
          {stage === 'email' ? (
            <>
              <label className="block"><span className="mb-1 block text-sm font-medium text-neutral-700">Your email address</span>
                <input type="email" autoComplete="email" className={inputClass} value={email} onChange={e => setEmail(e.target.value)} />
                {invite && <span className="mt-1 block text-xs text-neutral-500">The one the organisation gave: {invite.email_hint}</span>}
              </label>
              <Button onClick={sendCode} disabled={busy || !/\S+@\S+\.\S+/.test(email)} className="w-full">{busy ? 'Sending…' : 'Email me a code'}</Button>
            </>
          ) : (
            <>
              <p className="text-sm text-neutral-600">{message}</p>
              <label className="block"><span className="mb-1 block text-sm font-medium text-neutral-700">Six-digit code</span>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={`${inputClass} tracking-widest`} value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
              </label>
              <Button onClick={verify} disabled={busy || code.length !== 6} className="w-full">{busy ? 'Checking…' : 'Open the records'}</Button>
              <button className="text-sm text-neutral-500 underline" onClick={() => { setStage('email'); setCode(''); }}>Send another code</button>
            </>
          )}
          {error && <p className="text-sm text-red-700">{error}</p>}
          <p className="text-xs text-neutral-500">Read-only. The organisation can see which records you open.</p>
        </div>
      </Shell>
    );
  }

  if (!info || !data) return <Shell><p className="text-sm text-red-700">{error}</p></Shell>;
  const tz = data.report.timezone;
  const tabs: { key: TabKey; label: string; icon: ReactNode }[] = [
    { key: 'records', label: 'Records', icon: <FileText className="h-4 w-4" /> },
    ...(data.checklist ? [{ key: 'checklist' as const, label: 'Checklist', icon: <ClipboardCheck className="h-4 w-4" /> }] : []),
    { key: 'findings', label: 'Findings', icon: <MessageSquareWarning className="h-4 w-4" /> },
  ];

  return (
    <Shell>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="break-words text-xl font-semibold text-neutral-900">{info.organisation}</h1>
          <p className="break-words text-sm text-neutral-600">
            {info.title}{info.standard ? ` · ${info.standard}` : ''}
          </p>
          <p className="text-xs text-neutral-500">
            Records {formatLocalDateInTz(info.period_start, tz)} – {formatLocalDateInTz(info.period_end, tz)} · access until {formatDateTimeInTz(info.access_until, tz)}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => download('pdf')}><Download className="mr-1 h-4 w-4" />PDF</Button>
          {info.sections.some(s => s.key === 'spray') && (
            <Button size="sm" variant="outline" onClick={() => download('csv')}><FileSpreadsheet className="mr-1 h-4 w-4" />Spray CSV</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => { auditorService.signOut(); setData(null); setStage('email'); }}><LogOut className="mr-1 h-4 w-4" />Sign out</Button>
        </div>
      </div>
      {error && <p className="mb-3 text-sm text-red-700">{error}</p>}
      <Tabs tabs={tabs} active={tab} onChange={k => { setTab(k); if (k !== 'records') auditorService.markViewed(k); }} className="mb-4" />
      <div className="rounded-xl border border-neutral-200 bg-white p-4 sm:p-6">
        {tab === 'records' && <ComplianceReportView report={data.report} checks={data.checks} onSectionViewed={k => auditorService.markViewed(k)} />}
        {tab === 'checklist' && data.checklist && <ChecklistView checklist={data.checklist} />}
        {tab === 'findings' && <Findings sections={info.sections} tz={tz} />}
      </div>
    </Shell>
  );
}
